import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { loadOrganizationTarget } from '@saraa/organizations/server/abacTargets.js';
import { abacGuard } from '@saraa/permissions/abacMiddleware.js';
import { Organizations, Templates } from '@saraa/permissions/policies/index.js';
import { GenerateProcess } from '../entities/GenerateProcess.js';
import { GenerationLog } from '../entities/GenerationLog.js';
import { OrganizationGenerationStats } from '../entities/GenerationStats.js';
import { GenerationTask } from '../entities/GenerationTask.js';
import { Template } from '../entities/Template.js';
import { resolveGetOrganizationGenerationStats } from '../server/resolvers/resolveReadGenerationStats.js';
import { resolveStartDocumentGeneration } from '../server/resolvers/resolveStartDocumentGeneration.js';
import { resolveSubscribeGenerationLogs } from '../server/resolvers/resolveSubscribeGenerationLogs.js';
import { resolveSubscribeGenerationTasks } from '../server/resolvers/resolveSubscribeGenerationTasks.js';
import { resolveSubscribeTemplatesPaginatedFiltered } from '../server/resolvers/resolveReadTemplates.js';

const startDocumentGeneration = registerMutation({
  operationName: 'startDocumentGeneration',
  type: () => GenerateProcess,
  params: [{ name: 'process', type: () => GenerateProcess }],
  resolve: resolveStartDocumentGeneration,
});

const subscribeGenerationTasks = registerSubscription({
  operationName: 'subscribeGenerationTasks',
  type: () => [GenerationTask],
  params: [],
  resolve: resolveSubscribeGenerationTasks,
});

const subscribeGenerationLogs = registerSubscription({
  operationName: 'subscribeGenerationLogs',
  type: () => [GenerationLog],
  params: [{ name: 'limit', type: () => Int }],
  resolve: resolveSubscribeGenerationLogs,
});

const subscribeTemplatesPaginatedFiltered = registerSubscription({
  operationName: 'subscribeTemplatesPaginatedFiltered',
  type: () => [Template],
  params: [
    // Explicit rather than derived from the session: a user may belong to several
    // organizations and a run is pinned to exactly one.
    { name: 'organizationId', type: () => ID },
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => AdornisFilter },
  ],
  /**
   * The guard is what stops a caller naming an organization they do not belong
   * to. It rejects the subscription outright rather than letting the resolver
   * return an empty stream, so the client sees a real error instead of an
   * organization that looks like it has no templates.
   */
  middlewares: [
    abacGuard(Templates, 'view', (args: { organizationId: string }) => ({ organizationId: args.organizationId })),
  ],
  resolve: resolveSubscribeTemplatesPaginatedFiltered,
});

/**
 * How much an organization has generated, by month — what the activity card on its ERM detail draws.
 *
 * A query rather than a subscription: these are aggregated counts, which `watchQuery` cannot express,
 * and they are read when someone opens the organization rather than watched.
 *
 * Guarded on `view`, since reading how busy an organization has been is a read. The target is loaded
 * from the database by `loadOrganizationTarget` rather than taken from the caller, which is what stops
 * someone naming an organization they do not belong to and being told how much work goes through it.
 */
const getOrganizationGenerationStats = registerQuery({
  operationName: 'getOrganizationGenerationStats',
  type: () => OrganizationGenerationStats,
  params: [
    { name: 'organizationId', type: () => ID },
    { name: 'months', type: () => Int },
  ],
  middlewares: [
    abacGuard(Organizations, 'view', (args: { organizationId: string }) => loadOrganizationTarget(args.organizationId)),
  ],
  resolve: resolveGetOrganizationGenerationStats,
});

export const TemplaterAPI = {
  start: startDocumentGeneration,
  subscribeTasks: subscribeGenerationTasks,
  subscribeLogs: subscribeGenerationLogs,
  subcribeTemplates: subscribeTemplatesPaginatedFiltered,
  organizationStats: getOrganizationGenerationStats,
};
